import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Package,
  ChartBar,
  Warning,
  CheckCircle,
  TrendUp,
  Users,
  ArrowClockwise,
  Clock,
  ArrowRight,
  CurrencyDollar,
  Truck,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

function KpiCard({ title, value, sub, icon: Icon, tone = "default", testId }) {
  const tones = {
    default: "text-white",
    success: "text-[#10B981]",
    warning: "text-[#FACC15]",
    error: "text-[#FF3B30]",
    primary: "text-[#0055FF]",
  };
  return (
    <div
      data-testid={testId}
      className="bg-[#111111] border border-white/10 rounded-sm p-5 hover:border-white/20 transition-colors fade-up"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">
          {title}
        </div>
        <Icon size={20} className={tones[tone]} weight="duotone" />
      </div>
      <div className={`font-display text-4xl leading-none ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-2 text-xs text-white/50">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !stats) {
    return <div className="text-white/50 text-sm">Cargando panel…</div>;
  }

  const dailyChart = stats.daily.map((d) => ({
    day: d.day.slice(5),
    Citas: d.count,
  }));

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
            Panel · Operativo
          </div>
          <h1 className="font-display text-4xl sm:text-5xl">Dashboard</h1>
        </div>
        <button
          onClick={load}
          data-testid="dashboard-refresh"
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
        >
          <ArrowClockwise size={14} /> Actualizar
        </button>
      </div>

      {/* Pendientes hoy — quick action banner */}
      {stats.pending_today > 0 && (
        <Link
          to="/citas?status=pending&today=1"
          data-testid="pending-today-shortcut"
          className="block mb-6 group"
        >
          <div className="flex items-center gap-4 p-5 rounded-sm border border-[#FACC15]/40 bg-[#FACC15]/10 hover:bg-[#FACC15]/15 transition-colors">
            <div className="w-12 h-12 rounded-sm bg-[#FACC15] flex items-center justify-center shrink-0">
              <Clock size={24} color="#000" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#FACC15]/80">
                Acción requerida
              </div>
              <div className="font-display text-xl text-white mt-0.5">
                <span data-testid="pending-today-count" className="text-[#FACC15]">
                  {stats.pending_today}
                </span>{" "}
                {stats.pending_today === 1 ? "cita pendiente" : "citas pendientes"} de aprobar hoy
              </div>
              <div className="text-xs text-white/60 mt-1">
                Revísalas y aprueba o cancela para confirmar al proveedor.
              </div>
            </div>
            <div className="flex items-center gap-2 text-[#FACC15] text-sm font-semibold group-hover:translate-x-1 transition-transform">
              Revisar ahora <ArrowRight size={16} weight="bold" />
            </div>
          </div>
        </Link>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          testId="kpi-today"
          title="Citas del día"
          value={stats.today_count}
          sub={`${stats.total_appointments} totales`}
          icon={ChartBar}
          tone="primary"
        />
        <KpiCard
          testId="kpi-rejection-rate"
          title="% Rechazos"
          value={`${stats.rejection_rate}%`}
          sub={`${stats.rejected_count} de ${stats.total_products} productos`}
          icon={Warning}
          tone={stats.rejection_rate > 5 ? "error" : "success"}
        />
        <KpiCard
          testId="kpi-received"
          title="Unidades recibidas"
          value={Math.round(stats.received_units)}
          sub={`Esperadas: ${stats.expected_units}`}
          icon={Package}
          tone="success"
        />
        <KpiCard
          testId="kpi-pending-users"
          title="Usuarios pendientes"
          value={stats.pending_users}
          sub="Requieren aprobación"
          icon={Users}
          tone={stats.pending_users > 0 ? "warning" : "default"}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 bg-[#111111] border border-white/10 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">
                Citas por día · últimos 7
              </div>
              <div className="font-display text-lg mt-1">Actividad reciente</div>
            </div>
            <TrendUp size={20} className="text-[#0055FF]" />
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={dailyChart}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke="#666"
                  fontSize={10}
                  tick={{ fontFamily: "JetBrains Mono" }}
                />
                <YAxis
                  stroke="#666"
                  fontSize={10}
                  allowDecimals={false}
                  tick={{ fontFamily: "JetBrains Mono" }}
                />
                <Tooltip
                  contentStyle={{
                    background: "#111",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="Citas"
                  stroke="#0055FF"
                  strokeWidth={2}
                  dot={{ fill: "#0055FF", r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#111111] border border-white/10 rounded-sm p-5">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
            Top Proveedores
          </div>
          <div className="font-display text-lg mb-4">Con más citas</div>
          {stats.top_providers.length === 0 ? (
            <EmptyBlock text="Sin datos aún" />
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart
                  data={stats.top_providers}
                  layout="vertical"
                  margin={{ left: 0, right: 10 }}
                >
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" stroke="#666" fontSize={10} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#666"
                    fontSize={10}
                    width={90}
                    tick={{ fontFamily: "Manrope" }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#111",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="count" fill="#0055FF" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Reception + Status pies + Top brands rejected */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <PieCard
          title="Recepción"
          subtitle="Estado de descarga"
          data={Object.entries(stats.reception_breakdown || {}).map(([, v]) => ({
            name: v.label,
            value: v.count || 0,
            fill: v.color || "#A78BFA",
          }))}
        />
        <PieCard
          title="Aprobación"
          subtitle="Estado de la cita"
          data={[
            { name: "Pendiente", value: stats.status_breakdown?.pending || 0, fill: "#FACC15" },
            { name: "Aprobada", value: stats.status_breakdown?.approved || 0, fill: "#10B981" },
            { name: "Cancelada", value: stats.status_breakdown?.cancelled || 0, fill: "#FF3B30" },
          ]}
        />
        <div className="bg-[#111111] border border-white/10 rounded-sm p-5">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
            Marcas rechazadas
          </div>
          <div className="font-display text-lg mb-4">Top con más rechazos</div>
          {(stats.top_brands_rejected || []).length === 0 ? (
            <EmptyBlock text="Sin rechazos aún" />
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={stats.top_brands_rejected} layout="vertical" margin={{ left: 0, right: 10 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" stroke="#666" fontSize={10} allowDecimals={false} />
                  <YAxis type="category" dataKey="brand" stroke="#666" fontSize={10} width={90} tick={{ fontFamily: "Manrope" }} />
                  <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#FF3B30" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent rejections */}
      <div className="bg-[#111111] border border-white/10 rounded-sm">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">
              Últimos rechazos
            </div>
            <div className="font-display text-lg mt-1">Excepciones registradas</div>
          </div>
          <Warning size={20} className="text-[#FF3B30]" weight="duotone" />
        </div>
        {stats.recent_rejections.length === 0 ? (
          <div className="p-8">
            <EmptyBlock
              text="Sin rechazos registrados"
              sub="Los rechazos aparecerán aquí cuando se marquen en el detalle de una cita."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono-data uppercase tracking-widest text-white/40">
                <tr className="border-b border-white/10">
                  <th className="text-left px-5 py-3">Folio</th>
                  <th className="text-left px-5 py-3">SKU</th>
                  <th className="text-left px-5 py-3">Marca</th>
                  <th className="text-left px-5 py-3">Motivo</th>
                  <th className="text-right px-5 py-3">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_rejections.map((r, i) => (
                  <tr
                    key={`${r.folio}-${r.sku || i}-${r.created_at || i}`}
                    className="border-b border-white/5 bg-red-900/10 hover:bg-red-900/20 transition-colors"
                    data-testid="dashboard-rejection-row"
                  >
                    <td className="px-5 py-3 font-mono-data text-[#FF3B30] border-l-2 border-[#FF3B30]">
                      {r.folio}
                    </td>
                    <td className="px-5 py-3 font-mono-data text-white/90">{r.sku || "—"}</td>
                    <td className="px-5 py-3 text-white/80">{r.brand || "—"}</td>
                    <td className="px-5 py-3 text-white/60">{r.reason || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono-data text-white">{r.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PieCard({ title, subtitle, data }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm p-5">
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">{title}</div>
      <div className="font-display text-lg mb-2">{subtitle}</div>
      {total === 0 ? (
        <EmptyBlock text="Sin datos aún" />
      ) : (
        <>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#0a0a0a" strokeWidth={2}>
                  {data.map((d) => <Cell key={d.name} fill={d.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {data.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-white/70">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.fill }} />
                  {d.name}
                </div>
                <div className="font-mono-data text-white">{d.value}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyBlock({ text, sub }) {
  return (
    <div className="border border-dashed border-white/10 rounded-sm p-6 text-center">
      <CheckCircle size={28} className="text-white/20 mx-auto mb-2" weight="duotone" />
      <div className="text-white/60 text-sm">{text}</div>
      {sub && <div className="text-white/40 text-xs mt-1">{sub}</div>}
    </div>
  );
}
